import { customElement, property, query } from "lit/decorators.js";
import { AppComponent } from "../component";
import type { CreateComponentIdFunction } from "../types";
import { html, css } from "lit";
import { Toolbar, ToolbarItem } from "@omegagrid/toolbar";
import { ComponentFactory, ComponentId, ComponentStore, actions, dom, events } from "@omegagrid/core";

/** what an inner component may tell about its changes (`BaseEditor` does): their state, saving and discarding them */
type InnerEditor = HTMLElement & { isSaved?: boolean, save?: () => void|Promise<void>, discard?: () => void };

/**
 * A toolbar above one inner component. The toolbar values (dropdowns, a radio group of toggle buttons, ...) pick the id
 * of the inner component through `updateComponentId`, `createComponent` makes it. Every toolbar action is followed by
 * a `change` event carrying the toolbar values; a plain button also reaches the container as `toolbar.action.<id>`.
 *
 * The `edit` and `save` events of the inner components stop here: the container sends its own for the state of all
 * the components it holds, so the tab shows the unsaved changes of a component not shown too and `save()` saves them all.
 */
@customElement("rp-editor-container")
export class EditorContainer extends AppComponent {

	static styles = [AppComponent.styles, css`
		:host {
			height: 100%;
		}

		og-toolbar {
			flex: 0 0 auto;
			border-bottom: 1px solid var(--og-border-color);
		}

		.container {
			display: flex;
			height: 100%;
			flex-direction: column;
			position: relative;
		}

		.body {
			flex: 1;
			overflow: hidden;
		}
	`];

	@property({type: Array})
	items: ToolbarItem[] = [];

	@property({type: String})
	innerComponentId: ComponentId;

	@property()
	createComponent: ComponentFactory;

	@property()
	updateComponentId: CreateComponentIdFunction;

	/** keeps every inner component shown so far: a switch back shows the same instance, unsaved edits included */
	@property({type: Boolean})
	cacheComponents = false;

	@query('.body')
	body: HTMLDivElement;

	@query('og-toolbar')
	toolbar: Toolbar;

	private _store: ComponentStore;

	/** the inner components with unsaved changes: an `edit` event of a component adds it, a `save` event removes it */
	private _unsaved = new Set<InnerEditor>();

	/** no inner component holds unsaved changes */
	get isSaved() { return this._unsaved.size == 0 }

	/** the inner component shown now */
	get component() { return this.body?.firstElementChild as AppComponent | null }

	getComponent(id: ComponentId): Promise<HTMLElement> {
		if (!this.cacheComponents) return Promise.resolve(this.createComponent(id));
		this._store ??= new ComponentStore(id => this.createComponent(id));
		return this._store.get(id);
	}

	async updated() {
		this.showLoader();
		const component = await this.getComponent(this.innerComponentId) as InnerEditor;
		const previous = this.component as InnerEditor;
		if (component !== previous) {
			// without the cache the component replaced is gone, its unsaved changes with it
			if (previous && !this.cacheComponents) this.setUnsaved(previous, false);
			dom.empty(this.body);
			if (component) {
				this.body.appendChild(component);
				component.addEventListener('edit', this._onInnerEdit);
				component.addEventListener('save', this._onInnerEdit);
			}
		}
		if (!component && this.updateComponentId) {
			this.innerComponentId = this.updateComponentId(this.toolbar.values);
		}
		this.hideLoader();
	}

	layout() {
		this.component?.layout?.();
	}

	/** saves the changes of every inner component that holds some and can save them (the tab close prompt offers it) */
	async save() {
		await Promise.all(Array.from(this._unsaved, async component => {
			await component.save?.();
			// a component not shown is detached, its own `save` event does not reach the container
			if (component.isSaved ?? true) this.setUnsaved(component, false);
		}));
	}

	/** drops the changes of every inner component that holds some and can discard them */
	discard() {
		Array.from(this._unsaved).forEach(component => {
			component.discard?.();
			if (component.isSaved ?? true) this.setUnsaved(component, false);
		});
	}

	// the event of the inner component stops here, the container's own tells the state of all of them
	_onInnerEdit = (e: Event) => {
		e.stopPropagation();
		this.setUnsaved(e.currentTarget as InnerEditor, e.type == 'edit');
	}

	private setUnsaved(component: InnerEditor, unsaved: boolean) {
		const changed = unsaved ? !this._unsaved.has(component) : this._unsaved.delete(component);
		if (unsaved) this._unsaved.add(component);
		if (changed) this.dispatchEditEvent();
	}

	/** `edit` once an inner component has unsaved changes, `save` once none has - the host marks the tab by them */
	dispatchEditEvent() {
		this.dispatchEvent(new CustomEvent(this.isSaved ? 'save' : 'edit', {bubbles: true, composed: true}));
	}

	getValues() {
		return this.toolbar.values;
	}

	/** replaces the toolbar values (the controls re-render from them, a control without a value shows its item's own) and shows the component they pick */
	setValues(values: Record<string, unknown>) {
		this.toolbar.values = values;
		this.applyValues(values);
	}

	private applyValues(values: Record<string, unknown>) {
		if (this.updateComponentId) {
			this.innerComponentId = this.updateComponentId(values);
		}
		this.dispatchEvent(new events.ChangeEvent({value: values}));
	}

	_onToolbarAction = (e: actions.ActionEvent) => {
		this.applyValues(this.getValues());
	}

	render = () => html`
		<div class="container">
			<og-toolbar .items="${this.items}" @toolbar.action="${this._onToolbarAction}"></og-toolbar>
			<div class="body"></div>
		</div>
	`;

}
